// angular
import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule } from '@angular/core';
// shared
import { CookieStorage } from '@shared/storages/cookie.storage';
import { AppStorage } from '@shared/storages/universal.inject';
// components
import { AppComponent } from './app.component';
import { AppModule } from './app.module';
import { environment } from '../environments/environment';
import { TranslatesBrowserModule } from './translates/translates-browser';
import { REQUEST } from '@shared/tokens/server.tokens';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import * as Raven from 'raven-js';
import {
  ERROR_HANDLER_TYPES,
  expandShortUrl,
  SentryErrorHandler,
  ErrorLoggerHttpInterceptor,
  MODAL,
  SENTRY_IGNORE_ERRORS,
  SENTRY_IGNORE_URLS,
  sentryShouldSendCallback,
  WEB_UTILS_REQUEST
} from '@cakeiteasy/web-utils';
import { initPostHog, PostHogInitService } from '@cakeiteasy/order-process';

if (environment.raven.dsn) {
  Raven.config(environment.raven.dsn, {
    ignoreErrors: [MODAL.CLOSE_EVENTS.DISMISSED, MODAL.CLOSE_EVENTS.COMMON_CLOSE, ...SENTRY_IGNORE_ERRORS],
    ignoreUrls: SENTRY_IGNORE_URLS,
    shouldSendCallback: sentryShouldSendCallback
  })
    .install();
}

// the Request object only lives on the server
export function getRequest(): any {
  return { headers: { cookie: document.cookie } };
}

// since we support navigation by hash for modals,
// we need to remove hash to be sure, that we don't
// have a collision with an opened on init modal
export function removeHash(): Function {
  return (): Promise<void> => new Promise<void>((res) => {
    if (window.location.hash) {
      let withoutHash = document.location.href.replace(location.hash, '');
      window.history.replaceState({}, document.title, withoutHash);
    }

    res(null);
  });
}

// since we have short-cut urls for sms order-page, we need to format them
export function replaceForSms(): Function {
  return (): Promise<void> => new Promise<void>((res) => {
    try {
      const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
      const convertedUrl = expandShortUrl(window.location.href, baseHref);
      if (convertedUrl) {
        window.history.replaceState({}, document.title, convertedUrl);
      }
    } catch (e) {
      console.error(e);
    }

    res(null);
  });
}

@NgModule({
  bootstrap: [AppComponent],
  imports: [
    AppModule,
    TranslatesBrowserModule,
  ],
  providers: [
    { provide: ErrorHandler, useFactory: () => new SentryErrorHandler(Raven, ERROR_HANDLER_TYPES.RAVEN, environment, true, true) },
    {
      // The server provides these in main.server
      provide: REQUEST, useFactory: (getRequest)
    },
    {
      provide: WEB_UTILS_REQUEST, useFactory: (getRequest)
    },
    { provide: APP_INITIALIZER, useFactory: removeHash, multi: true },
    { provide: APP_INITIALIZER, useFactory: replaceForSms, multi: true },
    { provide: APP_INITIALIZER, useFactory: initPostHog, deps: [PostHogInitService], multi: true },
    { provide: AppStorage, useClass: CookieStorage },
    { provide: 'ORIGIN_URL', useValue: location.origin },
    { provide: 'AppConfig', useValue: environment },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorLoggerHttpInterceptor,
      multi: true,
    }
  ]
})
export class AppBrowserModule {
}
